import React, { useEffect, useState } from "react";
import { Link, NavLink, Navigate, Route, Routes, useLocation } from "react-router-dom";
import {
  AppShell as MantineAppShell,
  Group,
  NavLink as MantineNavLink,
  Title,
  Alert,
  Text,
  Stack,
  Anchor,
  UnstyledButton,
  Box,
  ActionIcon,
  Tooltip,
  useMantineColorScheme,
  useComputedColorScheme,
  Burger,
} from "@mantine/core";
import {
  IconLayoutDashboard,
  IconUsers,
  IconTool,
  IconCash,
  IconFileInvoice,
  IconCreditCard,
  IconServer,
  IconPackage,
  IconClock,
  IconFileText,
  IconSettings,
  IconSun,
  IconMoon,
  IconMenu2,
  IconUserShield,
  IconUserCircle,
} from "@tabler/icons-react";
import { api, Me, setToken } from "./api";
import { formatApiError } from "./format";
import { BrandLogo } from "./BrandLogo";
import { Button } from "./ui";
import { canDo, MeProvider, RequirePermission } from "./auth";
import { ProfileModal } from "./components/ProfileModal";

import ClientsPage from "./pages/ClientsPage";
import DashboardPage from "./pages/DashboardPage";
import BillingPage from "./pages/BillingPage";
import InvoicesPage from "./pages/InvoicesPage";
import PaymentsPage from "./pages/PaymentsPage";
import SettingsPage from "./pages/SettingsPage";
import NetworkPage from "./pages/NetworkPage";
import PlansPage from "./pages/PlansPage";
import LogsPage from "./pages/LogsPage";
import JobsPage from "./pages/JobsPage";
import InstallationsPage from "./pages/InstallationsPage";
import UsersPage from "./pages/UsersPage";

function getPageHeading(pathname: string): { kicker?: string; kickerTo?: string; title: string | null } {
  const path = pathname === "/" ? "/dashboard" : pathname;
  if (path === "/clients/new") return { kicker: "Clientes", kickerTo: "/clients", title: "Nuevo cliente" };
  if (/^\/clients\/[^/]+/.test(path)) return { kicker: "Clientes", kickerTo: "/clients", title: null };
  if (path.startsWith("/clients")) return { kicker: "Gestión", title: "Clientes" };
  if (path.startsWith("/dashboard")) return { kicker: "Panel", title: "Resumen" };
  if (path.startsWith("/installations")) return { kicker: "Operación", title: "Instalaciones" };
  if (path.startsWith("/billing")) return { kicker: "Operación", title: "Cobranza" };
  if (path.startsWith("/invoices")) return { kicker: "Facturación", title: "Facturas" };
  if (path.startsWith("/payments")) return { kicker: "Facturación", title: "Pagos" };
  if (path.startsWith("/network")) return { kicker: "Infraestructura", title: "Red" };
  if (path.startsWith("/plans")) return { kicker: "Infraestructura", title: "Planes" };
  if (path.startsWith("/jobs")) return { kicker: "Sistema", title: "Tareas" };
  if (path.startsWith("/logs")) return { kicker: "Sistema", title: "Logs" };
  if (path.startsWith("/users")) return { kicker: "Sistema", title: "Usuarios y roles" };
  if (path.startsWith("/settings")) return { kicker: "Sistema", title: "Configuración" };
  return { title: "Panel" };
}

// `id` es también el módulo del ACL que habilita la sección.
const NAV_ITEMS: { to: string; id: string; label: string; icon: React.ComponentType<{ size?: number | string; stroke?: number | string }> }[] = [
  { to: "/dashboard", id: "dashboard", label: "Inicio", icon: IconLayoutDashboard },
  { to: "/clients", id: "clients", label: "Clientes", icon: IconUsers },
  { to: "/installations", id: "installations", label: "Instalaciones", icon: IconTool },
  { to: "/billing", id: "billing", label: "Cobranza", icon: IconCash },
  { to: "/invoices", id: "invoices", label: "Facturas", icon: IconFileInvoice },
  { to: "/payments", id: "payments", label: "Pagos", icon: IconCreditCard },
  { to: "/network", id: "network", label: "Red", icon: IconServer },
  { to: "/plans", id: "plans", label: "Planes", icon: IconPackage },
  { to: "/jobs", id: "jobs", label: "Tareas / Crons", icon: IconClock },
  { to: "/logs", id: "logs", label: "Logs", icon: IconFileText },
  { to: "/users", id: "users", label: "Usuarios", icon: IconUserShield },
  { to: "/settings", id: "settings", label: "Configuración", icon: IconSettings },
];

export default function AppShell(props: { onLogout: () => void }) {
  const [me, setMe] = useState<Me | null>(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(0);
  const [safety, setSafety] = useState<{
    mikrotik_writes_disabled?: boolean;
    prod_host_overlap?: string[];
    servers_with_real_credentials?: number;
  } | null>(null);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("sc.sidebarCollapsed") === "1");
  const [mobileOpened, setMobileOpened] = useState(false);
  const loc = useLocation();
  const { toggleColorScheme } = useMantineColorScheme();
  const computedColorScheme = useComputedColorScheme("light");

  useEffect(() => {
    const next = localStorage.getItem("sc.sidebarCollapsed") === "1";
    setCollapsed(next);
  }, []);

  useEffect(() => {
    api
      .me()
      .then(setMe)
      .catch((e: unknown) => setError(formatApiError(e)));
  }, []);

  useEffect(() => {
    api
      .getSafetyStatus()
      .then((res) => setSafety(res as typeof safety))
      .catch(() => setSafety(null));
  }, []);

  useEffect(() => {
    const onLoading = (e: CustomEvent) => {
      const p = Number(e?.detail?.pending ?? 0);
      setPending(Number.isFinite(p) ? p : 0);
    };
    window.addEventListener("sc:loading", onLoading as EventListener);
    return () => window.removeEventListener("sc:loading", onLoading as EventListener);
  }, []);

  const pageHeading = getPageHeading(loc.pathname);
  const visibleNav = NAV_ITEMS.filter((t) => canDo(me, t.id, "view"));
  const homePath = visibleNav[0]?.to ?? "/dashboard";
  const guard = (module: string, el: React.ReactElement) => <RequirePermission module={module}>{el}</RequirePermission>;

  const toggleCollapsed = () => {
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem("sc.sidebarCollapsed", next ? "1" : "0");
  };

  return (
    <MantineAppShell
      header={{ height: 56 }}
      navbar={{
        width: collapsed ? 60 : 220,
        breakpoint: "sm",
        collapsed: { mobile: !mobileOpened, desktop: false },
      }}
      padding="md"
    >
      {pending > 0 ? (
        <Box pos="absolute" left={0} right={0} top={0} h={3} style={{ zIndex: 1055 }} className="sc-progress-top">
          <div className="sc-progress-top-bar" />
        </Box>
      ) : null}

      <MantineAppShell.Header>
        <Group h="100%" justify="space-between" px="md">
          <Group>
            <Burger
              opened={mobileOpened}
              onClick={() => setMobileOpened((o) => !o)}
              hiddenFrom="sm"
              size="sm"
              aria-label="Abrir menú"
            />
            <ActionIcon
              variant="subtle"
              color="gray"
              size="lg"
              onClick={toggleCollapsed}
              visibleFrom="sm"
              aria-label="Contraer menú"
            >
              <IconMenu2 size={20} />
            </ActionIcon>
            <NavLink to="/dashboard" style={{ textDecoration: "none", color: "inherit" }}>
              Inicio
            </NavLink>
          </Group>
          <Group>
            <Tooltip label={computedColorScheme === "dark" ? "Modo claro" : "Modo oscuro"}>
              <ActionIcon variant="default" size="lg" onClick={() => toggleColorScheme()} aria-label="Cambiar tema">
                {computedColorScheme === "dark" ? <IconSun size={18} /> : <IconMoon size={18} />}
              </ActionIcon>
            </Tooltip>
            <UnstyledButton onClick={() => setProfileOpen(true)} aria-label="Mi perfil">
              <Group gap={6} wrap="nowrap">
                <IconUserCircle size={20} />
                <Stack gap={0}>
                  <Text size="sm" fw={600} lh={1.1}>{me?.full_name || me?.username || "..."}</Text>
                  {me?.role ? <Text size="xs" c="dimmed" lh={1.1}>{me.role.name}</Text> : null}
                </Stack>
              </Group>
            </UnstyledButton>
            <Button
              variant="danger"
              onClick={async () => {
                try {
                  await api.logout();
                } catch {
                  // la sesión se cierra igual
                }
                setToken(null);
                props.onLogout();
              }}
            >
              Salir
            </Button>
          </Group>
        </Group>
      </MantineAppShell.Header>

      <MantineAppShell.Navbar p="xs">
        <MantineAppShell.Section>
          <Tooltip label="SistemaConnect" position="right" disabled={!collapsed}>
            <UnstyledButton
              component={Link}
              to="/dashboard"
              aria-label="SistemaConnect"
              style={{
                display: "flex",
                justifyContent: collapsed ? "center" : "flex-start",
                width: "100%",
                padding: collapsed ? "8px 0" : "8px 12px",
              }}
            >
              <BrandLogo mark={collapsed ? 28 : 32} wordmark={!collapsed} wordmarkSize={18} />
            </UnstyledButton>
          </Tooltip>
        </MantineAppShell.Section>
        <MantineAppShell.Section grow mt="md">
          {visibleNav.map((t) => {
            const isActive = loc.pathname === "/" ? t.to === "/dashboard" : loc.pathname.startsWith(t.to);
            const Icon = t.icon;
            if (collapsed) {
              // Modo contraído: solo el ícono centrado (estilo barra de actividad),
              // sin la doble caja NavLink + ThemeIcon.
              return (
                <Tooltip key={t.id} label={t.label} position="right" withArrow>
                  <ActionIcon
                    component={NavLink}
                    to={t.to}
                    onClick={() => setMobileOpened(false)}
                    variant={isActive ? "light" : "subtle"}
                    color={isActive ? "violet" : "gray"}
                    size={40}
                    radius="md"
                    aria-label={t.label}
                    style={{ display: "flex", margin: "0 auto 6px" }}
                  >
                    <Icon size={22} stroke={2} />
                  </ActionIcon>
                </Tooltip>
              );
            }
            return (
              <Box key={t.id} mb={2}>
                <MantineNavLink
                  component={NavLink}
                  to={t.to}
                  onClick={() => setMobileOpened(false)}
                  label={t.label}
                  leftSection={<Icon size={20} stroke={2} />}
                  active={isActive}
                  style={{ borderRadius: "var(--mantine-radius-sm)" }}
                />
              </Box>
            );
          })}
        </MantineAppShell.Section>
      </MantineAppShell.Navbar>

      <MantineAppShell.Main>
        {pageHeading.title ? (
          <Stack gap={2} mb="lg">
            {pageHeading.kicker ? (
              pageHeading.kickerTo ? (
                <Anchor component={Link} to={pageHeading.kickerTo} size="sm" c="dimmed" underline="never">
                  {pageHeading.kicker}
                </Anchor>
              ) : (
                <Text size="sm" c="dimmed">{pageHeading.kicker}</Text>
              )
            ) : null}
            <Title order={2} fw={600} lh={1.2}>
              {pageHeading.title}
            </Title>
          </Stack>
        ) : pageHeading.kicker ? (
          <Anchor component={Link} to={pageHeading.kickerTo || "/clients"} size="sm" c="dimmed" underline="never" display="inline-block" mb="md">
            {pageHeading.kicker}
          </Anchor>
        ) : null}

        {safety?.mikrotik_writes_disabled ? (
          <Alert color="teal" variant="light" mb="md" title="Staging: Mikrotik en solo lectura">
            Cortes, altas/bajas PPPoE y sync de perfiles están bloqueados hacia el router.
          </Alert>
        ) : (safety?.prod_host_overlap?.length ?? 0) > 0 || (safety?.servers_with_real_credentials ?? 0) > 0 ? (
          <Alert color="orange" variant="light" mb="md" title="Atención: Mikrotik de producción">
            {(safety?.prod_host_overlap?.length ?? 0) > 0
              ? `Hosts de prod detectados: ${safety!.prod_host_overlap!.join(", ")}. `
              : ""}
            {(safety?.servers_with_real_credentials ?? 0) > 0
              ? "Hay credenciales API cargadas. "
              : ""}
            Activá MIKROTIK_WRITES_DISABLED en staging antes de migrar o sincronizar.
          </Alert>
        ) : null}

        {error ? (
          <Alert color="red" mb="md" className="sc-error">
            {error}
          </Alert>
        ) : null}

        <MeProvider value={me}>
          {me ? (
            <Routes>
              <Route path="/" element={<Navigate to={homePath} replace />} />
              <Route path="/dashboard" element={guard("dashboard", <DashboardPage />)} />
              <Route path="/clients" element={guard("clients", <ClientsPage />)} />
              <Route path="/clients/new" element={guard("clients", <ClientsPage />)} />
              <Route path="/clients/:clientId" element={guard("clients", <ClientsPage />)} />
              <Route path="/installations" element={guard("installations", <InstallationsPage />)} />
              <Route path="/billing" element={guard("billing", <BillingPage />)} />
              <Route path="/invoices" element={guard("invoices", <InvoicesPage />)} />
              <Route path="/payments" element={guard("payments", <PaymentsPage />)} />
              <Route path="/network" element={guard("network", <NetworkPage />)} />
              <Route path="/network/:serverId" element={guard("network", <NetworkPage />)} />
              <Route path="/plans" element={guard("plans", <PlansPage />)} />
              <Route path="/jobs" element={guard("jobs", <JobsPage />)} />
              <Route path="/logs" element={guard("logs", <LogsPage />)} />
              <Route path="/users" element={guard("users", <UsersPage />)} />
              <Route path="/settings" element={guard("settings", <SettingsPage />)} />
              <Route path="*" element={<Navigate to={homePath} replace />} />
            </Routes>
          ) : null}
          <ProfileModal
            opened={profileOpen}
            onClose={() => setProfileOpen(false)}
            onSaved={(next) => {
              setMe(next);
            }}
          />
        </MeProvider>
      </MantineAppShell.Main>
    </MantineAppShell>
  );
}
